import type { BookingStep, AnalysisStep, BookingLanguage } from "./booking";
import type { MarketCountry } from "@/config/market";

/**
 * State snapshot captured at each step transition
 * This is stored in the database for each session state entry
 */
export interface SessionStateSnapshot {
  // Product selection
  selectedProducts: string[];
  selectedAddOns: string[];
  selectedPackageId: string | null;
  selectedPackageType: string;
  viewMode: "packages" | "configure";

  // Clinic and scheduling
  selectedClinic: string | null;
  selectedDate: string | null; // ISO date string
  wasDateManuallySelected?: boolean;
  selectedTimeSlots: Array<{
    date: string; // ISO date string
    time: string;
    isFastBooking?: boolean;
  }>;

  // Form data (full values stored, server is PII-cleared)
  formData: SessionFormData | null;

  // Stable client booking id (idempotency anchor shared by the browser submit
  // and the durable-booking Stripe webhook). Persisted so a TWINT/Klarna/PayPal
  // redirect — which full-page-reloads on return — restores the SAME id and the
  // returning browser submit dedupes with the webhook instead of double-booking.
  bookingId?: string | null;

  // Membership cadence flow: persisted so a refresh restores the membership
  // path (cadence step order, "Ahead membership" order line) instead of
  // reverting to the plain blood-test booking.
  membershipPlanActive?: boolean;
  // True only for the membership-first direction (blood tab → "Become a member").
  // Drives which step order is active (the 7-step membership funnel vs the 5-step
  // standard one), so it must survive a refresh alongside membershipPlanActive —
  // otherwise an MRI-first bundle and a membership-first booking become
  // indistinguishable after reload.
  membershipFirst?: boolean;
  // True when a membership is attached to a scan (the bundle). Persisted so the
  // product-step / tab-switch reset preserves it across a refresh — otherwise a
  // reload mid-bundle followed by back-navigation silently drops the membership.
  bundleAttached?: boolean;
  membershipPlan?: string;

  // Membership-first (book.aheadhealth.com), recorded before payment so a
  // visit that stops there can be followed up: the email the entry screen
  // took (PII, never sent to PostHog) and the plan screen's basket.
  entryEmail?: string | null;
  membershipFirstBasket?: {
    cadence: string;
    selectedAddOnIds: string[];
    attachedScanId: string | null;
  };

  // Details-page payment choice (pay-later vs become-a-member upsell,
  // AHD-7022). Context only — the store DEFAULTS this to "yearly" outside the
  // upsell surface, so it is NOT a membership marker on its own; use
  // membershipCheckoutInitiated below to classify membership purchases.
  selectedPaymentMode?: "yearly" | "once" | null;
  // Which membership path opened the Stripe checkout: the cadence flow
  // ("cadence") or the details-page upsell ("upsell"). Set right before the
  // embedded Payment Element opens and synced immediately, so a TWINT/Klarna/
  // PayPal redirect return restores it and the confirmation booking_step still
  // carries the membership marker. Cleared when the user backs out to a
  // non-membership payment mode. This is the only basket-level membership
  // signal for upsell purchases (membershipPlanActive covers only the cadence
  // flow).
  membershipCheckoutInitiated?: "cadence" | "upsell" | null;

  // The market (country) the flow runs in, so /analysis can show it without
  // opening the session. Absent on entries recorded before it was added.
  market?: MarketCountry;

  // UI state
  language: BookingLanguage;
  bookingType: "request";
}

/**
 * Form data captured in state snapshot
 */
export interface SessionFormData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  birthday?: string;
  genderAtBirth?: string;
  street?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  notes?: string;
  preferredLanguage?: string;
  // Consents stored as booleans
  gdprConsent?: boolean;
  tosAndPpConsent?: boolean;
  medicalProcessing?: boolean;
  anonymousDataUsage?: boolean;
}

/**
 * Request payload for record-session-state edge function
 */
export interface RecordSessionStateRequest {
  session_id: string;
  step_name: AnalysisStep;
  full_url: string;
  url_params: Record<string, string>;
  state: SessionStateSnapshot;
  is_completed?: boolean;
  language?: BookingLanguage;
  /** Flow marker: "membership-first" for the new app, absent for the current booking flow. */
  flow?: string;
}

/**
 * Response from record-session-state edge function
 */
export interface RecordSessionStateResponse {
  success: boolean;
  entry_id?: string;
  session_id?: string;
  message?: string;
}

/**
 * Session state entry as stored in the database
 */
export type SessionStateEntry = {
  id: string;
  session_id: string;
  step_name: AnalysisStep;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
  full_url: string | null;
  url_params: Record<string, string> | null;
  state: SessionStateSnapshot;
  is_completed: boolean;
  flow: string | null;
};

/**
 * Session summary as stored in the database
 */
export type SessionSummary = {
  session_id: string;
  first_ip: string | null;
  first_url: string | null;
  first_url_params: Record<string, string> | null;
  started_at: string;
  updated_at: string;
  last_step: AnalysisStep | null;
  max_step_reached: AnalysisStep | null;
  total_entries: number;
  is_completed: boolean;
  language: BookingLanguage | null;
  flow: string | null;
};

/**
 * Analysis user authorized to access /analysis portal
 */
export type AnalysisUser = {
  email: string;
  created_at: string;
};

/**
 * Step transition analytics data
 */
export type StepTransitionTime = {
  from_step: AnalysisStep;
  to_step: AnalysisStep;
  avg_seconds: number;
  median_seconds: number;
  transitions: number;
};

/**
 * Funnel data for visualization
 */
export type FunnelStep = {
  step: AnalysisStep;
  sessions_reached: number;
  drop_off_rate?: number;
};

/**
 * Marketing source analytics data
 */
export interface MarketingSourceData {
  source: string;
  medium: string | null;
  campaign: string | null;
  sessions: number;
  conversions: number;
  conversion_rate: number;
}

/**
 * Journey path for Sankey visualization
 */
export interface JourneyPath {
  path: AnalysisStep[];
  count: number;
}

/**
 * URL parameters to capture for analytics
 */
export const TRACKING_PARAMS = [
  // Google Ads
  "gclid",
  "gbraid",
  "wbraid",
  // UTM parameters
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  // Facebook
  "fbclid",
  // Microsoft Ads
  "msclkid",
  // TikTok
  "ttclid",
  // LinkedIn
  "li_fat_id",
  // General referral
  "ref",
  "referrer",
] as const;

export type TrackingParam = (typeof TRACKING_PARAMS)[number];

/**
 * Analysis filter for excluding IPs or user agents from dashboard
 */
export type AnalysisFilter = {
  id: string;
  filter_type: "ip" | "user_agent";
  pattern: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
};
